import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} HeroParallaxRefs
 * @property {HTMLElement} content - The hero content wrapper to apply the parallax lag to
 */

/**
 * Applies a scroll-linked lag to the hero's text content: as the page scrolls,
 * the content moves at a fraction of the scroll speed, so it visually falls
 * behind the rest of the page (and the hero's own background media).
 *
 * @extends {Component<HeroParallaxRefs>}
 */
class HeroParallax extends Component {
  requiredRefs = ['content'];

  /** @type {number | null} */
  #scrollRafId = null;

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updatePosition();
    });
  };

  #updatePosition() {
    const speed = Number(this.dataset.parallaxSpeed) || 0.4;
    const { top, height } = this.getBoundingClientRect();

    // How far the hero has scrolled past its natural position, clamped to
    // its own height so the offset stops growing once it's fully scrolled by.
    const progress = Math.min(Math.max(-top, 0), height);
    const offset = progress * speed;

    this.refs.content.style.setProperty('--hero-parallax-offset', `${offset}px`);
  }

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) return;

    document.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#updatePosition();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('scroll', this.#handleScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }
}

if (!customElements.get('hero-parallax')) {
  customElements.define('hero-parallax', HeroParallax);
}
